/**
 * Design tokens (SPEC §17.4, look per docs/DESIGN-REFERENCE.md / ADR-0008). The dark theme is the
 * Tradesea palette and the default; the light theme is Tradesea's light palette. Both are
 * validated against WCAG AA in CI (see contrast.test.ts), and tokens.css is checked against these
 * tables (tokens.test.ts). Side palettes are a user setting and override --buy/--sell only; side
 * is never encoded by colour alone.
 *
 * Token names kept from SPEC §17.4 (`bg-0..3`, `text-1..3`, …) because the chart engine reads
 * them (packages/chart/src/theme.ts); the Tradesea roles map onto them as documented per entry.
 */

export const darkTheme = {
  /** Page (Tradesea page). */
  "bg-0": "#0F0F0F",
  /** Panels / surface. */
  "bg-1": "#131314",
  /** Raised: menus, popovers, table headers, chips. */
  "bg-2": "#18191B",
  /** Hover / selected row; Tradesea outline & modal background. */
  "bg-3": "#242529",
  /** Subtle 1 px dividers inside panels (Tradesea outline). */
  outline: "#242529",
  /** Control borders and panel edges (Tradesea border). */
  border: "#36393C",
  /** Primary text. */
  "text-1": "#FAFAFA",
  /** Strong secondary text (values next to labels). */
  "text-2-strong": "#D8DCE4",
  /** Secondary text. */
  "text-2": "#BFC2C9",
  /** Muted: labels, headers, hints. */
  "text-3": "#9C9FA5",
  /** Faint: disabled glyphs, decorative separators. Never the only carrier of information. */
  "text-4": "#5A5D63",
  /** Focus, selection, primary UI actions (not trading). */
  accent: "#1858FF",
  /** Text colour on an accent fill. */
  "on-accent": "#FFFFFF",
  link: "#4F92FF",
  /** Buy / long / bid side / profit. */
  buy: "#0B9981",
  /** Buy text on panels: DOM bid sizes, P&L > 0. */
  "buy-text": "#00BB9C",
  /** Solid buy fill (buttons, DOM bid cells). */
  "buy-bg": "#006655",
  "buy-weak": "rgba(11,153,129,0.16)",
  /** Sell / short / ask side / loss. */
  sell: "#F23645",
  /** Solid sell fill. */
  "sell-bg": "#B5343F",
  "sell-bright": "#FF4D50",
  /** Sell text on panels: DOM ask sizes, P&L < 0. */
  "sell-text": "#F23645",
  "sell-weak": "rgba(242,54,69,0.16)",
  /** Label colour on solid buy/sell fills. */
  "on-side": "#FFFFFF",
  warn: "#F79009",
  danger: "#FF4D50",
  /**
   * Danger exits (Close, Reverse, Cancel all, Flatten): their own functional amber, never the buy or
   * sell colour (owner 2026-10-04). Outline buttons: text + border; Flatten: solid fill + label.
   */
  "danger-exit": "#F2C230",
  "danger-exit-bg": "#F2C230",
  "on-danger-exit": "#111111",
  info: "#4F92FF",
  last: "#FAFAFA",
  /** Chart grid lines, axis borders and pane separators (Tradesea chart axis). */
  "chart-grid": "#292A2C",
  /** Chart axis labels and legend (Tradesea chart text). */
  "chart-text": "#7F838B",
  /** Network Status indicator (Tradesea): Connected / Connecting… / unstable / Disconnected. */
  "net-connected": "#22C55E",
  "net-connecting": "#9CA3AF",
  "net-unstable": "#F59E0B",
  "net-disconnected": "#EF4444",
} as const;

export type TokenName = keyof typeof darkTheme;

/** Light theme: same token names, Tradesea light values (SPEC §17.4: separate table). */
export const lightTheme: Record<TokenName, string> = {
  "bg-0": "#F8FAFC",
  "bg-1": "#FFFFFF",
  "bg-2": "#F3F5F8",
  "bg-3": "#E8EBF0",
  outline: "#DCDFE5",
  border: "#CAD0D9",
  "text-1": "#1F2531",
  "text-2-strong": "#2E3542",
  "text-2": "#57606E",
  "text-3": "#646C7A",
  "text-4": "#98A0AC",
  accent: "#1A52E5",
  "on-accent": "#FFFFFF",
  link: "#1A52E5",
  buy: "#01A88C",
  "buy-text": "#007A66",
  "buy-bg": "#007A66",
  "buy-weak": "rgba(1,168,140,0.14)",
  sell: "#EC3342",
  "sell-bg": "#C8243A",
  "sell-bright": "#EC3342",
  "sell-text": "#C8243A",
  "sell-weak": "rgba(236,51,66,0.14)",
  "on-side": "#FFFFFF",
  warn: "#B54708",
  danger: "#C8102E",
  "danger-exit": "#755800",
  "danger-exit-bg": "#E6B800",
  "on-danger-exit": "#111111",
  info: "#1A52E5",
  last: "#1F2531",
  "chart-grid": "#E8EBF0",
  "chart-text": "#646C7A",
  "net-connected": "#15803D",
  "net-connecting": "#6B7280",
  "net-unstable": "#B45309",
  "net-disconnected": "#DC2626",
};

/**
 * "Glacis" workspace visual style (owner 2026-09-27, the MMT.GG-based platform in mockups 01/02):
 * near-black, flat, thin 1 px borders, cooler greys, blue accent, brighter green/red. Same token
 * names as the Tradesea tables, so every surface (and the chart engine) follows it without code
 * changes; density and the monospaced numerals live in `glacisDensity` (CSS only).
 */
export const glacisDarkTheme: Record<TokenName, string> = {
  "bg-0": "#050506",
  "bg-1": "#0B0B0C",
  "bg-2": "#121214",
  "bg-3": "#1B1B1E",
  outline: "#19191C",
  border: "#2E2E33",
  "text-1": "#E8E8EA",
  "text-2-strong": "#CFCFD4",
  "text-2": "#A9A9B0",
  "text-3": "#8C8C94",
  "text-4": "#4E4E55",
  accent: "#2F66E0",
  "on-accent": "#FFFFFF",
  link: "#6B9BFF",
  buy: "#22B573",
  "buy-text": "#34C77F",
  "buy-bg": "#0F6B45",
  "buy-weak": "rgba(34,181,115,0.16)",
  sell: "#F0484F",
  "sell-bg": "#A8323A",
  "sell-bright": "#FF5A5F",
  "sell-text": "#F2555C",
  "sell-weak": "rgba(240,72,79,0.16)",
  "on-side": "#FFFFFF",
  warn: "#E8A33C",
  danger: "#FF5A5F",
  "danger-exit": "#F2C230",
  "danger-exit-bg": "#F2C230",
  "on-danger-exit": "#111111",
  info: "#6B9BFF",
  last: "#E8E8EA",
  "chart-grid": "#1E1E22",
  "chart-text": "#85858D",
  "net-connected": "#22C55E",
  "net-connecting": "#9CA3AF",
  "net-unstable": "#F59E0B",
  "net-disconnected": "#EF4444",
};

/** Glacis, light variant: white flat panels, hairline greys, same accent family. */
export const glacisLightTheme: Record<TokenName, string> = {
  "bg-0": "#F4F4F5",
  "bg-1": "#FFFFFF",
  "bg-2": "#F7F7F8",
  "bg-3": "#ECECEE",
  outline: "#E4E4E7",
  border: "#D4D4D8",
  "text-1": "#111113",
  "text-2-strong": "#27272A",
  "text-2": "#52525B",
  "text-3": "#63636B",
  "text-4": "#A1A1AA",
  accent: "#2563EB",
  "on-accent": "#FFFFFF",
  link: "#1D4ED8",
  buy: "#15803D",
  "buy-text": "#11703A",
  "buy-bg": "#15803D",
  "buy-weak": "rgba(21,128,61,0.12)",
  sell: "#DC2626",
  "sell-bg": "#B91C1C",
  "sell-bright": "#DC2626",
  "sell-text": "#B91C1C",
  "sell-weak": "rgba(220,38,38,0.12)",
  "on-side": "#FFFFFF",
  warn: "#B45309",
  danger: "#B91C1C",
  "danger-exit": "#755800",
  "danger-exit-bg": "#E6B800",
  "on-danger-exit": "#111111",
  info: "#1D4ED8",
  last: "#111113",
  "chart-grid": "#ECECEE",
  "chart-text": "#63636B",
  "net-connected": "#15803D",
  "net-connecting": "#6B7280",
  "net-unstable": "#B45309",
  "net-disconnected": "#DC2626",
};

/**
 * "Tradovate" workspace visual style (owner 2026-09-29, reference screenshots 9/10 of the Tradovate
 * web platform; the default look since that date — Tradesea and Glacis stay selectable). Pure-black
 * chart and module bodies, dark-grey module headers and hairline separators, Tradovate's saturated
 * green / red (candles, Buy Mkt / Sell Mkt), orange for the highlighted instrument. Same token names
 * as the other tables; density lives in `tradovateDensity` (CSS only). Values are HELM's reading
 * of the screenshots ([I] in docs/DESIGN-REFERENCE.md), tuned to pass the same WCAG AA gate.
 */
export const tradovateDarkTheme: Record<TokenName, string> = {
  "bg-0": "#161616",
  "bg-1": "#000000",
  "bg-2": "#141414",
  "bg-3": "#242424",
  outline: "#1E1E1E",
  border: "#3A3A3A",
  "text-1": "#F0F0F0",
  "text-2-strong": "#D4D4D4",
  "text-2": "#B0B0B0",
  "text-3": "#8E8E8E",
  "text-4": "#555555",
  accent: "#2168D6",
  "on-accent": "#FFFFFF",
  link: "#5AA0FF",
  buy: "#2DB84C",
  "buy-text": "#3DD160",
  "buy-bg": "#1F7A34",
  "buy-weak": "rgba(45,184,76,0.18)",
  sell: "#F0323C",
  "sell-bg": "#C2242E",
  "sell-bright": "#FF4650",
  "sell-text": "#FF4A54",
  "sell-weak": "rgba(240,50,60,0.18)",
  "on-side": "#FFFFFF",
  warn: "#F5A623",
  danger: "#FF4650",
  "danger-exit": "#F2C230",
  "danger-exit-bg": "#F2C230",
  "on-danger-exit": "#111111",
  info: "#5AA0FF",
  last: "#F0F0F0",
  "chart-grid": "#1C1C1C",
  "chart-text": "#9A9A9A",
  "net-connected": "#22C55E",
  "net-connecting": "#9CA3AF",
  "net-unstable": "#F59E0B",
  "net-disconnected": "#EF4444",
};

/** Tradovate, light variant (Tradovate's light theme): white bodies, light-grey headers, same sides. */
export const tradovateLightTheme: Record<TokenName, string> = {
  "bg-0": "#ECEEF1",
  "bg-1": "#FFFFFF",
  "bg-2": "#F2F3F5",
  "bg-3": "#E6E8EC",
  outline: "#DDE0E5",
  border: "#C4C9D1",
  "text-1": "#15171A",
  "text-2-strong": "#2B2F36",
  "text-2": "#505662",
  "text-3": "#626874",
  "text-4": "#9CA2AC",
  accent: "#1D63D8",
  "on-accent": "#FFFFFF",
  link: "#1D5BC4",
  buy: "#16873A",
  "buy-text": "#117530",
  "buy-bg": "#16873A",
  "buy-weak": "rgba(22,135,58,0.12)",
  sell: "#D42A33",
  "sell-bg": "#C0222B",
  "sell-bright": "#D42A33",
  "sell-text": "#B81F28",
  "sell-weak": "rgba(212,42,51,0.12)",
  "on-side": "#FFFFFF",
  warn: "#B45309",
  danger: "#B81F28",
  "danger-exit": "#755800",
  "danger-exit-bg": "#E6B800",
  "on-danger-exit": "#111111",
  info: "#1D5BC4",
  last: "#15171A",
  "chart-grid": "#ECEEF1",
  "chart-text": "#626874",
  "net-connected": "#15803D",
  "net-connecting": "#6B7280",
  "net-unstable": "#B45309",
  "net-disconnected": "#DC2626",
};

/**
 * Workspace visual styles (setting `visual_style`). Tradovate is the default look since 2026-09-29
 * (owner); Tradesea (ADR-0008, the `:root` table) and Glacis stay selectable.
 */
export const VISUAL_STYLES = ["tradovate", "tradesea", "glacis"] as const;
export type VisualStyle = (typeof VISUAL_STYLES)[number];

/** Colour tables per visual style and light/dark theme. */
export const STYLE_THEMES: Record<
  VisualStyle,
  { dark: Record<TokenName, string>; light: Record<TokenName, string> }
> = {
  tradesea: { dark: darkTheme, light: lightTheme },
  glacis: { dark: glacisDarkTheme, light: glacisLightTheme },
  tradovate: { dark: tradovateDarkTheme, light: tradovateLightTheme },
};

/**
 * CSS selector carrying each style's colour table (tokens.css). Glacis dark must not win over its
 * light block, hence the `:not`.
 */
export const STYLE_SELECTORS: Record<VisualStyle, { dark: string; light: string }> = {
  tradesea: { dark: ":root", light: ':root[data-theme="light"]' },
  glacis: {
    dark: ':root[data-style="glacis"]:not([data-theme="light"])',
    light: ':root[data-style="glacis"][data-theme="light"]',
  },
  tradovate: {
    dark: ':root[data-style="tradovate"]:not([data-theme="light"])',
    light: ':root[data-style="tradovate"][data-theme="light"]',
  },
};

/**
 * Glacis density (CSS-only tokens under `:root[data-style="glacis"]`): monospaced numerals, 20 px
 * rows, 2 px radii, flat (no card rounding). Mirrored in tokens.css and checked by tokens.test.ts.
 */
export const glacisDensity = {
  "font-num": '"JetBrains Mono", "SF Mono", ui-monospace, Menlo, Consolas, "Roboto Mono", monospace',
  "row-h": "20px",
  radius: "2px",
  "radius-card": "0px",
  "radius-popover": "4px",
  "panel-pad": "6px",
  "fs-ui": "12px",
  "fs-dense": "11px",
} as const;

/**
 * Tradovate density (CSS-only tokens under `:root[data-style="tradovate"]`): compact 20 px rows,
 * 2 px radii, flat panels, Inter numerals (Tradovate shows proportional sans figures, not mono).
 * Mirrored in tokens.css and checked by tokens.test.ts.
 */
export const tradovateDensity = {
  "row-h": "20px",
  radius: "2px",
  "radius-card": "0px",
  "radius-popover": "3px",
  "panel-pad": "6px",
  "fs-ui": "12px",
  "fs-dense": "11px",
} as const;

/** Buy/sell palettes (SPEC §17.4). Green/Red is the Tradesea pair. */
export const sidePalettes = {
  greenRed: { buy: "#0B9981", sell: "#F23645" },
  blueRed: { buy: "#2F7CF6", sell: "#F23645" },
  colorBlindSafe: { buy: "#1E88E5", sell: "#FF8F00" },
} as const;

export type SidePalette = keyof typeof sidePalettes;

/** Typography scale in px (SPEC §17.4). */
export const fontSize = { dense: 11, ui: 12, title: 13, dialog: 14, kpi: 16, kpiLarge: 20 } as const;

/** Font stacks: Inter for UI, Rubik (tabular figures) for numbers (Tradesea). Self-hosted. */
export const fontFamily = {
  ui: '"Inter Variable", "Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  num: '"Rubik Variable", "Rubik", "Inter Variable", system-ui, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, Consolas, monospace',
} as const;

/** 4 px grid, control heights, radii (SPEC §17.4; radii per Tradesea). */
export const spacing = { grid: 4, panelPadding: 8, rowHeight: 24, rowHeightCompact: 20 } as const;
export const controlHeight = { sm: 24, md: 28, lg: 32 } as const;
export const radius = { control: 3, card: 8, pill: 999 } as const;

/** Render a theme as CSS custom properties on a selector. */
export function themeToCss(selector: string, theme: Record<TokenName, string>): string {
  const body = (Object.keys(theme) as TokenName[]).map((k) => `  --${k}: ${theme[k]};`).join("\n");
  return `${selector} {\n${body}\n}\n`;
}

export * from "./schemes.ts";
